import { reactive, computed } from 'vue'

import router from '../router'
import { reportResult, reportShow, reportUse } from '../util/report'
import { extractVideoUrl, pollTaskAPI, enhancePromptAPI, uploadFile, fetchQuota, checkActivity } from '../api'
import { trackedFetch } from '../util/apiReport'
import { createSession, createRound, listSessions, getSession, deleteSession as apiDeleteSession } from '../api/session'
import { tabQueries, fallbackImgs, videoModels, promptPools } from '../data/prompts'
import { useModal } from './useModal'
import demoMaterials from '../data/demoMaterials.json'

const { toast, showQuotaModal } = useModal()
import type {
  ApiRequestError,
  AppStoreState,
  CdnStatus,
  ConversationRound,
  CreateRoundRequest,
  DetailVideo,
  GenParams,
  HistoryItem,
  MaterialTabKey,
  PageId,
  DemoMaterial,
  PexelsVideo,
  RoundStatus,
  UploadedImage,
  VideoRound,
} from '../types'
import { assistantMessageForStatus } from '../types'

const API_BASE = 'https://docer-api.wps.cn/ai-docer/aivideo/api'
const STORAGE_KEY = 'wps_video_history'
const MAX_IMAGES = 9
const MAX_VIDEOS = 2
const MAX_AUDIOS = 3
const MAX_VIDEO_SIZE = 50 * 1024 * 1024
const MAX_AUDIO_SIZE = 15 * 1024 * 1024
const MAX_VIDEO_DURATION = 15
const MAX_AUDIO_DURATION = 15

type ResolvedDemoMaterial = DemoMaterial & {
  imageUrl: string
  videoUrl: string
  referenceUrls: string[]
}

const demoVideoModules = import.meta.glob('../assets/ai-video/**/*.mp4', {
  eager: true,
  import: 'default',
}) as Record<string, string>

function resolveVideo(relPath: string): string {
  return demoVideoModules[`../assets/ai-video/${relPath}`] || ''
}

const resolvedDemoMaterials: ResolvedDemoMaterial[] = (demoMaterials as DemoMaterial[]).map((item) => ({
  ...item,
  imageUrl: item.image_url || item.image_base64 || '',
  videoUrl: item.video_url || resolveVideo(item.video),
  referenceUrls: item.reference_urls || item.references_base64 || [],
}))

const demoById = new Map<string, ResolvedDemoMaterial>(
  resolvedDemoMaterials.map((item) => [item.id, item])
)

/** 从会话页点「重新编辑」返回首页后，下一轮生成的埋点类型 */
let nextGenAfterReEdit = false

const localDemoVideos: PexelsVideo[] = resolvedDemoMaterials.map((item, idx) => ({
  id: 900000 + idx,
  demo_id: item.id,
  image: item.imageUrl,
  prompt_text: item.text,
  video_files: [{ quality: 'hd', link: item.videoUrl }],
  user: { name: item.title || '本地素材' },
  duration: 10,
}))

function mapApiRoundStatus(status: string): RoundStatus {
  if (status === 'succeeded') return 'succeeded'
  if (status === 'failed') return 'failed'
  if (status === 'cancelled') return 'cancelled'
  if (status === 'analyzing') return 'analyzing'
  if (status === 'specify_target') return 'specify_target'
  if (status === 'not_video') return 'not_video'
  return 'pending'
}

function applyTerminalIntentStatus(local: ConversationRound, status: RoundStatus): boolean {
  if (status !== 'specify_target' && status !== 'not_video') return false
  local.status = status
  local.assistantMessage = assistantMessageForStatus(status)
  local.statusLabel = undefined
  local.progress = 0
  state.isGenerating = false
  void loadQuota()
  return true
}

function getHistory(): HistoryItem[] {
  try {
    return JSON.parse(localStorage.getItem(STORAGE_KEY) || '[]') as HistoryItem[]
  } catch {
    return []
  }
}

function saveHistory(list: HistoryItem[]): void {
  localStorage.setItem(STORAGE_KEY, JSON.stringify(list))
}

function addToHistory(item: HistoryItem): void {
  const h = getHistory()
  const existing = h.findIndex((x) => x.id === item.id)
  if (existing >= 0) h[existing] = item
  else h.unshift(item)
  saveHistory(h)
}

function nowStr(): string {
  return new Date().toLocaleString('zh-CN', {
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
    hour: '2-digit',
    minute: '2-digit',
  })
}

const state = reactive<AppStoreState>({
  currentPage: 'home',
  uploadedImages: [],
  uploadedVideos: [],
  uploadedAudios: [],
  uploadedImageData: null,
  mentionDropOpen: false,
  conversationRounds: [],
  roundCounter: 0,
  isGenerating: false,
  uploadProgress: { phase: 'idle', total: 0, completed: 0 },

  selectedRatio: '3:4',
  selectedRes: '720P',
  selectedDuration: '5s',
  selectedStyle: 'default',
  selectedStyleLabel: '默认',
  selectedCamera: 'free',
  selectedCameraLabel: '自由运镜',
  selectedModel: videoModels[0].key,
  selectedModelLabel: videoModels[0].label,

  pexelsData: [],
  currentPexelsQuery: 'cinematic trending',
  currentPexelsPage: 1,
  currentTab: 'hot',
  pexelsLoading: false,

  openDropdown: null,

  currentSuggestion: '',
  ghostHtml: '',
  showTabHint: false,

  isEnhancing: false,
  watermark: true,

  materialDetailOpen: false,
  currentDetailIndex: -1,
  detailVideo: null,

  videoModalOpen: false,
  videoModalUrl: '',

  promptText: '',

  // Session management
  currentSessionId: null,
  sessions: [],
  sessionsLoading: false,

  sessionUseEditAgain: false,
  sessionUseGenerateAgain: false,
  sessionUseHotPrompt: false,
  sessionGenerateRounds: 0,
  sessionSucceededVideos: 0,

  quota: { total: 0, used: 0, hold: 0, available: 0 },
  activityEligible: true,
  activityMessage: '',
})

const DRAFT_KEY = 'wps_video_draft'

function saveDraftBeforeLogin(): void {
  try {
    sessionStorage.removeItem(DRAFT_KEY)
    sessionStorage.setItem(DRAFT_KEY, JSON.stringify({ promptText: state.promptText }))
  } catch { /* ignore */ }
}

function clearDraft(): void {
  sessionStorage.removeItem(DRAFT_KEY)
}

function restoreDraftAfterLogin(): void {
  const raw = sessionStorage.getItem(DRAFT_KEY)
  if (!raw) return
  sessionStorage.removeItem(DRAFT_KEY)
  try {
    const draft = JSON.parse(raw)
    if (draft.promptText) state.promptText = draft.promptText
    if (Array.isArray(draft.uploadedImages) && draft.uploadedImages.length > 0) {
      state.uploadedImages = draft.uploadedImages
      state.uploadedImageData = state.uploadedImages[0]?.data || null
    }
  } catch { /* ignore */ }
}

const hasContent = computed(
  () =>
    state.promptText.trim().length > 0 ||
    state.uploadedImages.length > 0 ||
    state.uploadedVideos.length > 0 ||
    state.uploadedAudios.length > 0,
)

function resetSessionUsageTracking(): void {
  state.sessionUseEditAgain = false
  state.sessionUseGenerateAgain = false
  state.sessionUseHotPrompt = false
  state.sessionGenerateRounds = 0
  state.sessionSucceededVideos = 0
  nextGenAfterReEdit = false
}

function reportVideoUse(round: ConversationRound): void {
  reportUse({
    use_edit_again: state.sessionUseEditAgain ? 1 : 0,
    use_generate_again: state.sessionUseGenerateAgain ? 1 : 0,
    use_hotprompt_generate: state.sessionUseHotPrompt ? 1 : 0,
    generate_rounds: state.sessionGenerateRounds,
    generate_counts: state.sessionSucceededVideos,
    watermark: (round.watermark !== false) ? 1 : 0,
    od: round.model || state.selectedModelLabel,
  })
}

function showPage(p: PageId, sessionId?: string): void {
  state.currentPage = p
  if (p === 'home') {
    revokeAllPreviewUrls()
    state.uploadedImages = []
    state.promptText = ''
  }
  if (p === 'session') {
    revokeAllPreviewUrls()
    state.uploadedImages = []
    state.uploadedVideos = []
    state.uploadedAudios = []
    state.uploadedImageData = null
    state.promptText = ''
  }
  if (p === 'session' && sessionId) {
    state.currentSessionId = sessionId
    const target = `/session/${sessionId}`
    if (router.currentRoute.value.path !== target) {
      router.push(target)
    }
  } else if (p === 'session' && state.currentSessionId) {
    const target = `/session/${state.currentSessionId}`
    if (router.currentRoute.value.path !== target) {
      router.push(target)
    }
  } else {
    const target = p === 'home' ? '/' : '/session'
    if (router.currentRoute.value.path !== target) {
      router.push(target)
    }
  }
  window.scrollTo(0, 0)
  if (p === 'session') {
    reportShow('detail')
  }
}

// --- Session management ---

async function loadSessions(): Promise<void> {
  state.sessionsLoading = true
  try {
    state.sessions = await listSessions()
  } catch (e) {
    console.warn('[sessions] load failed:', e)
  } finally {
    state.sessionsLoading = false
  }
}

async function openSession(sessionId: string): Promise<void> {
  resetSessionUsageTracking()
  state.currentSessionId = sessionId
  state.conversationRounds = []
  try {
    const data = await getSession(sessionId)
    state.conversationRounds = data.rounds.map((r) => {
      let params: GenParams = { ratio: '3:4', resolution: '720P', duration: '5s' }
      let wm = true
      try {
        const raw = JSON.parse(r.params)
        params = {
          ratio: raw.ratio || params.ratio,
          resolution: (raw.resolution || raw.size || params.resolution).toUpperCase().replace(/P$/i, '') + 'P',
          duration: String(raw.duration || '5').replace(/s$/i, '') + 's',
        }
        if (typeof raw.watermark === 'boolean') wm = raw.watermark
      } catch { /* keep defaults */ }
      const mappedStatus = mapApiRoundStatus(r.status)
      const hasTaskId = !!r.provider_task_id
      const isRunning = r.status === 'running' || r.status === 'pending'
      const videoUrl = r.video_url || null
      const assistantMessage = assistantMessageForStatus(mappedStatus)
      return {
        id: r.id,
        prompt: r.prompt,
        actualPrompt: r.actual_prompt || undefined,
        params,
        taskId: r.provider_task_id,
        videoUrl,
        coverUrl: r.cover_url || null,
        status: videoUrl ? 'succeeded' : mappedStatus,
        assistantMessage,
        imagesUsed: (() => {
          if (!r.image_ref) return []
          try { const arr = JSON.parse(r.image_ref); return Array.isArray(arr) ? arr : [r.image_ref] } catch { return [r.image_ref] }
        })(),
        videosUsed: (() => {
          try {
            const raw = JSON.parse(r.params)
            if (raw.input_videos?.length) return raw.input_videos.map((v: { video_url: string }) => v.video_url)
          } catch { /* ignore */ }
          return undefined
        })(),
        audiosUsed: (() => {
          try {
            const raw = JSON.parse(r.params)
            if (raw.audio_urls?.length) return raw.audio_urls
          } catch { /* ignore */ }
          return undefined
        })(),
        model: r.model,
        watermark: wm,
        statusLabel:
          mappedStatus === 'analyzing'
            ? '正在分析您的需求...'
            : r.error_message || (isRunning && hasTaskId ? '视频生成中...' : undefined),
        intent: r.intent || undefined,
        referenceVideoUrl: r.reference_video_url || undefined,
        createdAt: r.created_at || undefined,
        cdnStatus: resolveCdnStatusForSessionRound(mappedStatus, videoUrl, r.cdn_status),
        downgrade_status: r.downgrade_status || undefined,
        downgrade_notice: r.downgrade_notice || undefined,
      } as ConversationRound
    })
    // Sync watermark state from the first round of this session
    if (state.conversationRounds.length > 0) {
      state.watermark = state.conversationRounds[0].watermark !== false
    }

    // Resume polling for any running/pending/analyzing rounds
    for (const round of state.conversationRounds) {
      if (round.status === 'succeeded' && round.taskId && !isCdnTerminal(round.cdnStatus)) {
        void pollCdnStatus(round.taskId, round.id)
        continue
      }
      if (round.status === 'specify_target' || round.status === 'not_video') continue
      if (round.status !== 'pending' && round.status !== 'analyzing') continue
      state.isGenerating = true
      if (round.taskId) {
        pollTask(round.taskId, round.id)
      } else {
        waitAndPoll(round.id, sessionId)
      }
    }
    showPage('session', sessionId)
  } catch (e) {
    console.error('[session] open failed:', e)
    state.currentSessionId = null
    state.conversationRounds = []
    showPage('home')
    toast.warning('会话不存在或无权访问')
  }
}

async function deleteSessionById(sessionId: string): Promise<void> {
  try {
    await apiDeleteSession(sessionId)
    state.sessions = state.sessions.filter((s) => s.id !== sessionId)
    if (state.currentSessionId === sessionId) {
      state.currentSessionId = null
      state.conversationRounds = []
      showPage('home')
    }
  } catch (e) {
    console.error('[session] delete failed:', e)
  }
}

function createNewSession(): void {
  state.currentSessionId = null
  state.conversationRounds = []
  showPage('home')
}

function toggleDropdown(id: string): void {
  state.openDropdown = state.openDropdown === id ? null : id
}

function closeAllDropdowns(): void {
  state.openDropdown = null
  state.mentionDropOpen = false
}

function updateRatio(v: string): void {
  state.selectedRatio = v
  closeAllDropdowns()
}

function updateRes(v: string): void {
  state.selectedRes = v
}

function updateDuration(v: string): void {
  state.selectedDuration = v
  closeAllDropdowns()
}

function updateStyle(key: string, label: string): void {
  state.selectedStyle = key
  state.selectedStyleLabel = label
  closeAllDropdowns()
}

function updateCamera(key: string, label: string): void {
  state.selectedCamera = key
  state.selectedCameraLabel = label
  closeAllDropdowns()
}

function updateModel(key: string, label: string): void {
  state.selectedModel = key
  state.selectedModelLabel = label
  closeAllDropdowns()
}

const ALLOWED_IMAGE_TYPES = ['image/png', 'image/jpeg', 'image/webp', 'image/gif']
const ALLOWED_VIDEO_TYPES = ['video/mp4', 'video/quicktime']
const ALLOWED_AUDIO_TYPES = ['audio/wav', 'audio/mpeg', 'audio/mp3']
const MAX_IMAGE_SIZE = 10 * 1024 * 1024 // 10MB

const VIDEO_EXT = new Set(['.mp4', '.mov'])
const AUDIO_EXT = new Set(['.wav', '.mp3'])

function fileExt(name: string): string {
  const i = name.lastIndexOf('.')
  return i >= 0 ? name.slice(i).toLowerCase() : ''
}

function classifyUploadFile(file: File): 'image' | 'video' | 'audio' | null {
  if (ALLOWED_IMAGE_TYPES.includes(file.type)) return 'image'
  if (ALLOWED_VIDEO_TYPES.includes(file.type) || VIDEO_EXT.has(fileExt(file.name))) return 'video'
  if (ALLOWED_AUDIO_TYPES.includes(file.type) || AUDIO_EXT.has(fileExt(file.name))) return 'audio'
  return null
}

function newUploadId(prefix: string): string {
  return prefix + Date.now() + '_' + Math.random().toString(36).substring(2, 11)
}

function getMediaDuration(file: File, kind: 'video' | 'audio'): Promise<number> {
  return new Promise((resolve, reject) => {
    const url = URL.createObjectURL(file)
    const el = document.createElement(kind)
    el.preload = 'metadata'
    el.onloadedmetadata = () => {
      URL.revokeObjectURL(url)
      if (!Number.isFinite(el.duration)) {
        reject(new Error('无法读取媒体时长'))
        return
      }
      resolve(el.duration)
    }
    el.onerror = () => {
      URL.revokeObjectURL(url)
      reject(new Error('无法读取媒体时长'))
    }
    el.src = url
  })
}

function revokePreviewUrl(data: string): void {
  if (data.startsWith('blob:')) URL.revokeObjectURL(data)
}

function revokeAllPreviewUrls(): void {
  for (const img of state.uploadedImages) revokePreviewUrl(img.data)
  for (const vid of state.uploadedVideos) revokePreviewUrl(vid.data)
  for (const aud of state.uploadedAudios) revokePreviewUrl(aud.data)
}

async function resolveImageBlob(img: UploadedImage): Promise<Blob> {
  if (img.file) return img.file
  const resp = await fetch(img.data)
  if (!resp.ok) throw new Error(`fetch image failed: ${resp.status}`)
  return resp.blob()
}

/** 上传参考素材到 CDN；失败返回 null */
async function uploadAllAttachments(): Promise<{
  imageUrls: string[] | null
  videoUrls: string[] | null
  audioUrls: string[] | null
}> {
  const images = state.uploadedImages
  const videos = state.uploadedVideos
  const audios = state.uploadedAudios
  const total = images.length + videos.length + audios.length
  if (total === 0) {
    return { imageUrls: [], videoUrls: [], audioUrls: [] }
  }

  state.uploadProgress = { phase: 'uploading', total, completed: 0 }
  const bump = (): void => { state.uploadProgress.completed++ }

  const imageResults = await Promise.allSettled(
    images.map(async (img) => {
      if (img.cdnUrl) {
        bump()
        return img.cdnUrl
      }
      if (img.data.startsWith('http://') || img.data.startsWith('https://')) {
        img.cdnUrl = img.data
        bump()
        return img.data
      }
      const blob = await resolveImageBlob(img)
      const url = await uploadFile(blob, img.name || 'image.jpg')
      img.cdnUrl = url
      bump()
      return url
    }),
  )

  const videoResults = await Promise.allSettled(
    videos.map(async (vid) => {
      if (vid.cdnUrl) {
        bump()
        return vid.cdnUrl
      }
      if (!vid.file) throw new Error('missing video file')
      const url = await uploadFile(vid.file, vid.name || 'video.mp4')
      vid.cdnUrl = url
      bump()
      return url
    }),
  )

  const audioResults = await Promise.allSettled(
    audios.map(async (aud) => {
      if (aud.cdnUrl) {
        bump()
        return aud.cdnUrl
      }
      if (!aud.file) throw new Error('missing audio file')
      const url = await uploadFile(aud.file, aud.name || 'audio.mp3')
      aud.cdnUrl = url
      bump()
      return url
    }),
  )

  state.uploadProgress = { phase: 'idle', total: 0, completed: 0 }

  const failed =
    imageResults.filter((r) => r.status === 'rejected').length +
    videoResults.filter((r) => r.status === 'rejected').length +
    audioResults.filter((r) => r.status === 'rejected').length
  if (failed > 0) {
    toast.error(`有 ${failed} 个文件上传失败，请重试`)
    return { imageUrls: null, videoUrls: null, audioUrls: null }
  }

  return {
    imageUrls: imageResults.map((r) => (r as PromiseFulfilledResult<string>).value),
    videoUrls: videoResults.map((r) => (r as PromiseFulfilledResult<string>).value),
    audioUrls: audioResults.map((r) => (r as PromiseFulfilledResult<string>).value),
  }
}

function handleImageUpload(file: File | null | undefined): void {
  if (!file) return
  void handleMediaUpload([file])
}

async function handleMediaUpload(files: File[]): Promise<void> {
  if (!files.length) return

  const imageFiles: File[] = []
  const videoFiles: File[] = []
  const audioFiles: File[] = []
  for (const file of files) {
    const kind = classifyUploadFile(file)
    if (kind === 'image') imageFiles.push(file)
    else if (kind === 'video') videoFiles.push(file)
    else if (kind === 'audio') audioFiles.push(file)
    else toast.warning(`文件 "${file.name}" 格式不支持`)
  }

  if (imageFiles.length) await addImageFiles(imageFiles)
  if (videoFiles.length) await addVideoFiles(videoFiles)
  if (audioFiles.length) await addAudioFiles(audioFiles)
}

async function addImageFiles(files: File[]): Promise<void> {
  const remaining = MAX_IMAGES - state.uploadedImages.length
  if (remaining <= 0) {
    toast.warning(`最多只能上传 ${MAX_IMAGES} 张图片`)
    return
  }
  if (files.length > remaining) {
    toast.warning(`最多上传 ${MAX_IMAGES} 张图片，已自动截取前 ${remaining} 张`)
    files = files.slice(0, remaining)
  }
  for (const file of files) {
    if (!ALLOWED_IMAGE_TYPES.includes(file.type)) {
      toast.warning(`图片 "${file.name}" 格式不支持，仅支持 PNG、JPG、WebP、GIF`)
      continue
    }
    if (file.size > MAX_IMAGE_SIZE) {
      toast.warning(`图片 "${file.name}" 超出 10MB 限制，已跳过`)
      continue
    }
    const img: UploadedImage = {
      id: newUploadId('img_'),
      name: file.name,
      data: URL.createObjectURL(file),
      file,
    }
    state.uploadedImages.push(img)
    state.uploadedImageData = state.uploadedImages[0]?.data || null
  }
}

async function addVideoFiles(files: File[]): Promise<void> {
  const remaining = MAX_VIDEOS - state.uploadedVideos.length
  if (remaining <= 0) {
    toast.warning(`最多只能上传 ${MAX_VIDEOS} 个视频`)
    return
  }
  if (files.length > remaining) {
    toast.warning(`最多上传 ${MAX_VIDEOS} 个视频，已自动截取前 ${remaining} 个`)
    files = files.slice(0, remaining)
  }
  for (const file of files) {
    if (!ALLOWED_VIDEO_TYPES.includes(file.type) && !VIDEO_EXT.has(fileExt(file.name))) {
      toast.warning(`视频 "${file.name}" 格式不支持，仅支持 MP4、MOV`)
      continue
    }
    if (file.size > MAX_VIDEO_SIZE) {
      toast.warning(`视频 "${file.name}" 超出 50MB 限制，已跳过`)
      continue
    }
    let duration: number
    try {
      duration = await getMediaDuration(file, 'video')
    } catch {
      toast.warning(`视频 "${file.name}" 无法读取时长，已跳过`)
      continue
    }
    if (duration > MAX_VIDEO_DURATION) {
      toast.warning(`视频 "${file.name}" 时长超过 15 秒，已跳过`)
      continue
    }
    state.uploadedVideos.push({
      id: newUploadId('vid_'),
      name: file.name,
      data: URL.createObjectURL(file),
      file,
      duration,
    })
  }
}

async function addAudioFiles(files: File[]): Promise<void> {
  const remaining = MAX_AUDIOS - state.uploadedAudios.length
  if (remaining <= 0) {
    toast.warning(`最多只能上传 ${MAX_AUDIOS} 个音频`)
    return
  }
  if (files.length > remaining) {
    toast.warning(`最多上传 ${MAX_AUDIOS} 个音频，已自动截取前 ${remaining} 个`)
    files = files.slice(0, remaining)
  }
  for (const file of files) {
    if (!ALLOWED_AUDIO_TYPES.includes(file.type) && !AUDIO_EXT.has(fileExt(file.name))) {
      toast.warning(`音频 "${file.name}" 格式不支持，仅支持 WAV、MP3`)
      continue
    }
    if (file.size > MAX_AUDIO_SIZE) {
      toast.warning(`音频 "${file.name}" 超出 15MB 限制，已跳过`)
      continue
    }
    let duration: number
    try {
      duration = await getMediaDuration(file, 'audio')
    } catch {
      toast.warning(`音频 "${file.name}" 无法读取时长，已跳过`)
      continue
    }
    if (duration > MAX_AUDIO_DURATION) {
      toast.warning(`音频 "${file.name}" 时长超过 15 秒，已跳过`)
      continue
    }
    state.uploadedAudios.push({
      id: newUploadId('aud_'),
      name: file.name,
      data: URL.createObjectURL(file),
      file,
      duration,
    })
  }
}

/** @deprecated use handleMediaUpload */
function handleMultiImageUpload(files: File[]): void {
  void handleMediaUpload(files)
}

function checkHotPromptReset(): void {
  if (state.sessionUseHotPrompt && !state.promptText.trim() &&
    state.uploadedImages.length === 0 && state.uploadedVideos.length === 0 && state.uploadedAudios.length === 0) {
    state.sessionUseHotPrompt = false
  }
}

function removeUploadedImage(id: string): void {
  const removed = state.uploadedImages.find((img) => img.id === id)
  if (removed) revokePreviewUrl(removed.data)
  state.uploadedImages = state.uploadedImages.filter((img) => img.id !== id)
  state.uploadedImageData = state.uploadedImages[0]?.data || null
  checkHotPromptReset()
}

function removeUploadedVideo(id: string): void {
  const removed = state.uploadedVideos.find((vid) => vid.id === id)
  if (removed) revokePreviewUrl(removed.data)
  state.uploadedVideos = state.uploadedVideos.filter((vid) => vid.id !== id)
  checkHotPromptReset()
}

function removeUploadedAudio(id: string): void {
  const removed = state.uploadedAudios.find((aud) => aud.id === id)
  if (removed) revokePreviewUrl(removed.data)
  state.uploadedAudios = state.uploadedAudios.filter((aud) => aud.id !== id)
  checkHotPromptReset()
}

function clearUploadedImage(): void {
  revokeAllPreviewUrls()
  state.uploadedImages = []
  state.uploadedVideos = []
  state.uploadedAudios = []
  state.uploadedImageData = null
  checkHotPromptReset()
}

function toggleMentionDrop(): void {
  if (state.uploadedImages.length === 0 && state.uploadedVideos.length === 0 && state.uploadedAudios.length === 0) {
    toast.info('请先上传素材')
    return
  }
  state.mentionDropOpen = !state.mentionDropOpen
}

function insertImageMention(name: string): void {
  state.promptText += '@' + name + ' '
  state.mentionDropOpen = false
}

function getCurrentParams(): GenParams {
  return {
    ratio: state.selectedRatio,
    resolution: state.selectedRes,
    duration: state.selectedDuration,
  }
}

function buildRoundRequest(
  prompt: string,
  params: GenParams,
  images?: string[],
  videoUrls?: string[],
  audioUrls?: string[],
): CreateRoundRequest {
  const dur = params.duration.replace('s', '')
  return {
    prompt,
    duration: dur,
    size: params.resolution.toLowerCase(),
    ratio: params.ratio,
    model: state.selectedModel || undefined,
    reference_images: images?.length ? images : undefined,
    input_videos: videoUrls?.length ? videoUrls.map((video_url) => ({ video_url })) : undefined,
    audio_urls: audioUrls?.length ? audioUrls : undefined,
    watermark: state.watermark,
  }
}

function onPromptInput(text: string): void {
  state.promptText = text
  checkHotPromptReset()
}

function onPromptKeydown(_e: KeyboardEvent): boolean {
  return false
}

function clearGhostSuggestion(): void {
  state.currentSuggestion = ''
  state.ghostHtml = ''
  state.showTabHint = false
}

async function enhancePrompt(): Promise<void> {
  const text = state.promptText.trim()
  if (!text) return

  const { useAuth } = await import('./useAuth')
  saveDraftBeforeLogin()
  if (!useAuth().requireLogin()) return

  state.isEnhancing = true
  clearGhostSuggestion()
  let firstChunk = true
  try {
    const data = await enhancePromptAPI(text, (chunk) => {
      if (firstChunk) {
        state.promptText = chunk
        firstChunk = false
      } else {
        state.promptText += chunk
      }
    })
    if (data.enhanced) {
      state.promptText = data.enhanced
      toast.success('Prompt 已优化')
    } else if (firstChunk) {
      toast.warning('未能生成优化建议，请尝试修改描述后重试')
    }
  } catch {
    if (firstChunk) state.promptText = text
    toast.warning('Prompt 优化服务暂不可用，请稍后重试')
  } finally {
    state.isEnhancing = false
  }
}

function roundReportParams(round?: ConversationRound | null): Record<string, unknown> {
  if (!round) return {}
  return {
    watermark: round.watermark ? 1 : 0,
    video_ratio: round.params.ratio,
    video_pixels: round.params.resolution,
    video_time: round.params.duration,
    video_image: round.imagesUsed?.length ?? 0,
  }
}

function apiErrorMessage(err: unknown): string {
  if (err && typeof err === 'object' && 'serverError' in err) {
    const m = (err as ApiRequestError).serverError
    if (typeof m === 'string') return m
  }
  if (err instanceof Error) return err.message
  return String(err)
}

function isQuotaExhaustedError(err: unknown): boolean {
  return err != null && typeof err === 'object' && 'errorCode' in err &&
    (err as ApiRequestError).errorCode === 'quota_exhausted'
}

async function loadQuota(): Promise<void> {
  try {
    const info = await fetchQuota()
    state.quota = info
  } catch (e) {
    console.warn('[quota] load failed:', e)
  }
}

function redirectToAccessDenied(): void {
  if (router.currentRoute.value.name === 'access-denied') return
  router.replace('/access-denied')
}

async function loadActivity(): Promise<void> {
  if (router.currentRoute.value.name === 'access-denied') return
  try {
    const result = await checkActivity()
    state.activityEligible = result.eligible
    state.activityMessage = result.message || ''
    if (!result.eligible) {
      redirectToAccessDenied()
    }
  } catch (e) {
    console.warn('[activity] load failed:', e)
  }
}

function ensureActivityEligible(): boolean {
  if (state.activityEligible) return true
  redirectToAccessDenied()
  return false
}

async function createRoundWithRetry(
  sessionId: string,
  roundReq: CreateRoundRequest,
): Promise<{ roundData: VideoRound; sessionId: string }> {
  try {
    const roundData = await createRound(sessionId, roundReq)
    return { roundData, sessionId }
  } catch (err: unknown) {
    const is404 = err instanceof Error && err.message.includes('404')
    if (!is404) throw err
    const sess = await createSession(roundReq.prompt?.substring(0, 50))
    state.currentSessionId = sess.id
    state.sessions.unshift(sess)
    state.conversationRounds = []
    const roundData = await createRound(sess.id, roundReq)
    return { roundData, sessionId: sess.id }
  }
}

async function startGen(): Promise<void> {
  if (state.isGenerating) return
  const prompt = state.promptText.trim()
  const hasVisualMedia =
    state.uploadedImages.length > 0 ||
    state.uploadedVideos.length > 0
  if (!prompt && !hasVisualMedia) return

  if (state.uploadedAudios.length > 0 && !hasVisualMedia) {
    toast.warning('音频不能单独作为输入，请同时上传图片或视频')
    return
  }

  const { useAuth } = await import('./useAuth')
  saveDraftBeforeLogin()
  if (!useAuth().requireLogin()) return
  if (!ensureActivityEligible()) return

  state.isGenerating = true
  const { checkBeforeGenerate } = await import('./useAiAccess')
  const canGen = await checkBeforeGenerate(
    (loading) => { if (!loading) state.isGenerating = loading },
    (msg) => { toast.warning(msg) },
  )
  if (!canGen) { state.isGenerating = false; return }

  const params = getCurrentParams()
  const effectivePrompt = prompt || '根据上传的参考素材生成一段精美的动态视频'
  const genAttemptStartedAt = Date.now()

  try {
    // Always create a new session from the homepage; reuse only inside a session page
    let sessionId = state.currentPage === 'home' ? '' : state.currentSessionId
    if (!sessionId) {
      const wasHotPrompt = state.sessionUseHotPrompt
      resetSessionUsageTracking()
      if (wasHotPrompt) state.sessionUseHotPrompt = true
      const sess = await createSession(effectivePrompt.substring(0, 50))
      sessionId = sess.id
      state.currentSessionId = sessionId
      state.sessions.unshift(sess)
      state.conversationRounds = []
    }

    const attachments = await uploadAllAttachments()
    if (attachments.imageUrls === null) {
      state.isGenerating = false
      return
    }

    const roundReq = buildRoundRequest(
      effectivePrompt,
      params,
      attachments.imageUrls.length ? attachments.imageUrls : undefined,
      attachments.videoUrls?.length ? attachments.videoUrls : undefined,
      attachments.audioUrls?.length ? attachments.audioUrls : undefined,
    )

    const result = await createRoundWithRetry(sessionId, roundReq)
    sessionId = result.sessionId
    const roundData = result.roundData
    const round: ConversationRound = {
      id: roundData.id,
      prompt: effectivePrompt,
      params,
      taskId: roundData.provider_task_id || '',
      videoUrl: null,
      status: 'analyzing',
      imagesUsed: attachments.imageUrls,
      videosUsed: attachments.videoUrls || undefined,
      audiosUsed: attachments.audioUrls || undefined,
      model: state.selectedModel,
      watermark: state.watermark,
      statusLabel: '正在分析您的需求...',
      createdAt: new Date().toISOString(),
      genStartedAt: Date.now(),
      genReportType: nextGenAfterReEdit ? 'edit_again' : 'generate',
    }
    if (nextGenAfterReEdit) nextGenAfterReEdit = false
    revokeAllPreviewUrls()
    state.uploadedImages = []
    state.uploadedVideos = []
    state.uploadedAudios = []
    state.uploadedImageData = null
    state.conversationRounds.push(round)
    state.sessionGenerateRounds++
    state.roundCounter++

    addToHistory({
      id: roundData.id,
      prompt: effectivePrompt,
      params,
      taskId: roundData.provider_task_id || '',
      videoUrl: null,
      status: 'pending',
      createdAt: new Date().toISOString(),
    })
    showPage('session', sessionId)
    clearDraft()

    if (state.quota.total > 0 && state.quota.available > 0) {
      state.quota.available--
      state.quota.hold++
    }

    waitAndPoll(roundData.id, sessionId)
  } catch (err) {
    console.error('[startGen] 请求失败:', err)
    if (isQuotaExhaustedError(err)) {
      showQuotaModal()
      void loadQuota()
    }
    const label = '❌ ' + apiErrorMessage(err)
    reportResult({
      type: 'generate',
      result: 'fail',
      fail_reason: apiErrorMessage(err),
      time: Date.now() - genAttemptStartedAt,
      video_ratio: params.ratio,
      video_pixels: params.resolution,
      video_time: params.duration,
      video_image: state.uploadedImages.length,
    })
    state.conversationRounds.push({
      id: 'r_' + Date.now(),
      prompt: effectivePrompt,
      params,
      taskId: '',
      videoUrl: null,
      status: 'failed',
      statusLabel: label,
    })
    if (!isQuotaExhaustedError(err) && state.currentSessionId) showPage('session', state.currentSessionId)
    state.isGenerating = false
  }
}

async function waitAndPoll(roundId: string, sessionId: string): Promise<void> {
  const lr0 = state.conversationRounds.find((lr) => lr.id === roundId)
  if (lr0 && !lr0.genStartedAt) {
    lr0.genStartedAt = Date.now()
    if (!lr0.genReportType) lr0.genReportType = 'generate'
  }
  // The server creates the task asynchronously; poll the round until task_id appears.
  // Stop only when no valid data is returned for a long time.
  let taskId = ''
  const NO_DATA_TIMEOUT = 20 * 60 * 1000 // 20 minutes
  const TASK_ID_POLL_INTERVAL = 30 * 1000 // 30 seconds
  const FIRST_POLL_DELAY = 3 * 1000       // first poll after 3s
  const startAt = Date.now()
  let isFirstPoll = true
  for (; ;) {
    await sleep(isFirstPoll ? FIRST_POLL_DELAY : TASK_ID_POLL_INTERVAL)
    isFirstPoll = false
    try {
      const data = await getSession(sessionId)
      const r = data.rounds.find((rd) => rd.id === roundId)
      if (r) {
        const local = state.conversationRounds.find((lr) => lr.id === roundId)
        if (local) {
          const mappedStatus = mapApiRoundStatus(r.status)
          if (mappedStatus === 'analyzing') {
            local.status = 'analyzing'
            local.statusLabel = '正在分析您的需求...'
          } else if (applyTerminalIntentStatus(local, mappedStatus)) {
            return
          } else if (mappedStatus === 'pending' || r.status === 'running') {
            local.status = 'pending'
            if (r.provider_task_id) {
              local.statusLabel = '视频生成中...'
            } else {
              const elapsedSec = Math.floor((Date.now() - startAt) / 1000)
              local.statusLabel =
                elapsedSec < 60 ? '排队中...' : `排队中...已等待${Math.floor(elapsedSec / 60)}分钟`
            }
            if (r.intent) local.intent = r.intent
            if (r.reference_video_url) local.referenceVideoUrl = r.reference_video_url
          }
        }
        if (r.provider_task_id) {
          taskId = r.provider_task_id
          const local = state.conversationRounds.find((lr) => lr.id === roundId)
          if (local) {
            local.taskId = taskId
            local.status = 'pending'
            local.statusLabel = '视频生成中...'
            if (r.intent) local.intent = r.intent
            if (r.reference_video_url) local.referenceVideoUrl = r.reference_video_url
          }
          break
        }
        if (r && r.status === 'failed') {
          const local = state.conversationRounds.find((lr) => lr.id === roundId)
          if (local && r.video_url) {
            applyVideoWithCdnTransferFailure(local, r.video_url)
            state.isGenerating = false
            return
          }
          if (local) {
            local.status = 'failed'
            local.statusLabel = '❌ ' + (r.error_message || '生成失败')
            reportResult({
              type: local.genReportType || 'generate',
              result: 'fail',
              fail_reason: r.error_message || '生成失败',
              time: Date.now() - (local.genStartedAt || Date.now()),
              ...roundReportParams(local),
            })
          }
        }
        if (r.status === 'cancelled') {
          const local = state.conversationRounds.find((lr) => lr.id === roundId)
          if (local) local.status = 'cancelled'
          state.isGenerating = false
          void loadQuota()
          return
        }
      }
    } catch { /* retry */ }
    if (Date.now() - startAt >= NO_DATA_TIMEOUT) {
      const local = state.conversationRounds.find((lr) => lr.id === roundId)
      if (local) {
        local.status = 'failed'
        local.statusLabel = '生成超时，请重新尝试'
        local.progress = 0
        reportResult({
          type: local.genReportType || 'generate',
          result: 'fail',
          fail_reason: local.statusLabel,
          time: Date.now() - (local.genStartedAt || startAt),
          ...roundReportParams(local),
        })
      }
      state.isGenerating = false
      void loadQuota()
      return
    }
  }
  if (taskId) {
    pollTask(taskId, roundId)
    return
  }
}

const sleep = (ms: number): Promise<void> => new Promise((r) => setTimeout(r, ms))

const CDN_POLL_INTERVAL = 7 * 1000

function normalizeCdnStatus(raw?: string): CdnStatus {
  if (raw === 'uploading' || raw === 'done' || raw === 'failed') return raw
  return ''
}

function isCdnTerminal(status?: CdnStatus): boolean {
  return status === 'done' || status === 'failed'
}

function applyCdnStatus(round: ConversationRound, raw?: string): void {
  if (raw === undefined) return
  round.cdnStatus = normalizeCdnStatus(raw)
}

/** 任务接口失败但 round/会话仍有视频链接 → 视为 CDN 转存失败，继续展示视频 */
function applyVideoWithCdnTransferFailure(round: ConversationRound, videoUrl: string): void {
  round.videoUrl = videoUrl
  round.status = 'succeeded'
  round.progress = 100
  round.statusLabel = '生成完成!'
  round.cdnStatus = 'failed'
}

function applyDowngradeFields(
  local: ConversationRound,
  raw?: { downgrade_status?: string; downgrade_notice?: string },
): void {
  if (!raw) return
  if (raw.downgrade_status) local.downgrade_status = raw.downgrade_status
  if (raw.downgrade_notice) local.downgrade_notice = raw.downgrade_notice
}

function resolveCdnStatusForSessionRound(
  mappedStatus: RoundStatus,
  videoUrl: string | null,
  rawCdnStatus?: string,
): CdnStatus {
  const cdn = normalizeCdnStatus(rawCdnStatus)
  if (videoUrl && mappedStatus === 'failed' && cdn !== 'done') return 'failed'
  return cdn
}

/** 视频生成成功后继续轮询 CDN 转存状态 */
async function pollCdnStatus(taskId: string, roundId: string): Promise<void> {
  for (; ;) {
    const round = state.conversationRounds.find((r) => r.id === roundId)
    if (!round || round.status !== 'succeeded') return
    if (isCdnTerminal(round.cdnStatus)) return

    await sleep(CDN_POLL_INTERVAL)

    const current = state.conversationRounds.find((r) => r.id === roundId)
    if (!current || current.status !== 'succeeded') return
    if (isCdnTerminal(current.cdnStatus)) return

    try {
      const data = await pollTaskAPI(taskId)
      if (data.status === 'failed') {
        current.cdnStatus = 'failed'
        return
      }
      applyCdnStatus(current, data.cdn_status)
      if (isCdnTerminal(current.cdnStatus)) return
    } catch {
      /* 网络抖动时继续轮询 */
    }
  }
}

async function cancelGeneration(roundId: string): Promise<void> {
  const round = state.conversationRounds.find((r) => r.id === roundId)
  if (!round || (round.status !== 'pending' && round.status !== 'analyzing')) return
  round.status = 'cancelled'
  round.statusLabel = '已取消'
  round.progress = 0
  state.isGenerating = false
  reportResult({
    type: round.genReportType || 'generate',
    result: 'stop',
    time: Date.now() - (round.genStartedAt || Date.now()),
    ...roundReportParams(round),
  })

  try {
    await trackedFetch(`${API_BASE}/rounds/${roundId}/cancel`, {
      method: 'POST',
      credentials: 'include',
      headers: { 'Content-Type': 'application/json' },
    })
  } catch { /* best-effort */ }
  void loadQuota()
}

async function pollTask(taskId: string, roundId: string): Promise<string | null> {
  const POLL_INTERVAL = 30 * 1000    // 30s per poll
  const NO_DATA_TIMEOUT = 20 * 60 * 1000     // stop only if no data for 20min
  const MAX_CONSECUTIVE_ERRORS = 5
  let consecutiveErrors = 0
  let lastDataAt = Date.now()

  const round0 = state.conversationRounds.find((r) => r.id === roundId)
  if (round0 && !round0.genStartedAt) round0.genStartedAt = Date.now()
  if (round0 && !round0.genReportType) round0.genReportType = 'generate'

  // Simulated progress: smoothly grows but never reaches 100 until done
  let fakeProgress = 5

  for (; ;) {
    // Check if round was cancelled by user
    const cancelCheck = state.conversationRounds.find((r) => r.id === roundId)
    if (cancelCheck?.status === 'cancelled') {
      console.log('[pollTask] round cancelled, stopping poll:', roundId)
      state.isGenerating = false
      void loadQuota()
      return null
    }

    // Stop only when no successful data for a long time.
    if (Date.now() - lastDataAt > NO_DATA_TIMEOUT) {
      const round = state.conversationRounds.find((r) => r.id === roundId)
      if (round) {
        round.status = 'failed'
        round.statusLabel = '生成超时，请重新尝试'
        round.progress = 0
        reportResult({
          type: round.genReportType || 'generate',
          result: 'fail',
          fail_reason: round.statusLabel,
          time: Date.now() - (round.genStartedAt || lastDataAt),
          ...roundReportParams(round),
        })
      }
      state.isGenerating = false
      void loadQuota()
      return null
    }

    await sleep(POLL_INTERVAL)

    // Advance fake progress
    if (fakeProgress < 30) fakeProgress += Math.random() * 8 + 3
    else if (fakeProgress < 60) fakeProgress += Math.random() * 5 + 2
    else if (fakeProgress < 85) fakeProgress += Math.random() * 3 + 1
    else if (fakeProgress < 95) fakeProgress += Math.random() * 1.5
    fakeProgress = Math.min(fakeProgress, 95)

    try {
      const data = await pollTaskAPI(taskId)
      lastDataAt = Date.now()
      consecutiveErrors = 0
      const status = data.status || 'unknown'
      const round = state.conversationRounds.find((r) => r.id === roundId)

      if (round) {
        round.progress = Math.round(fakeProgress)

        if (status === 'running' || status === 'processing') {
          round.statusLabel = '视频生成中...'
        } else if (status === 'queued') {
          round.statusLabel = '排队中...'
        }

        if (data.cover_url) round.coverUrl = data.cover_url
        if (data.actual_prompt) round.actualPrompt = data.actual_prompt
        applyDowngradeFields(round, data)
        if (status === 'succeeded') {
          applyCdnStatus(round, data.cdn_status)
        }
      }

      if (status === 'succeeded') {
        const videoUrl = extractVideoUrl(data)
        if (round) {
          round.videoUrl = videoUrl
          round.coverUrl = data.cover_url || round.coverUrl || null
          round.actualPrompt = data.actual_prompt || round.actualPrompt
          round.status = 'succeeded'
          round.progress = 100
          round.statusLabel = '生成完成!'
          applyCdnStatus(round, data.cdn_status)
          applyDowngradeFields(round, data)
          const elapsed = Date.now() - (round.genStartedAt || Date.now())
          const type = round.genReportType || 'generate'
          state.sessionSucceededVideos++
          reportResult({
            type,
            result: 'success',
            time: elapsed,
            ...roundReportParams(round),
          })
        }
        addToHistory({
          id: roundId,
          prompt: round?.prompt || '',
          params: round?.params || getCurrentParams(),
          taskId,
          videoUrl,
          status: 'succeeded',
          createdAt: new Date().toISOString(),
        })
        state.isGenerating = false
        void loadQuota()
        if (round && !isCdnTerminal(round.cdnStatus)) {
          void pollCdnStatus(taskId, roundId)
        }
        return videoUrl
      }

      if (status === 'failed') {
        const fallbackVideoUrl = extractVideoUrl(data) || round?.videoUrl || null
        if (fallbackVideoUrl && round) {
          applyVideoWithCdnTransferFailure(round, fallbackVideoUrl)
          round.coverUrl = data.cover_url || round.coverUrl || null
          round.actualPrompt = data.actual_prompt || round.actualPrompt
          state.sessionSucceededVideos++
          reportResult({
            type: round.genReportType || 'generate',
            result: 'success',
            time: Date.now() - (round.genStartedAt || Date.now()),
            ...roundReportParams(round),
          })
          state.isGenerating = false
          void loadQuota()
          return fallbackVideoUrl
        }
        const errMsg = data.error || '生成失败，请换个 Prompt 再试'
        if (round) {
          round.status = 'failed'
          round.statusLabel = '❌ ' + errMsg
          round.progress = 0
          reportResult({
            type: round.genReportType || 'generate',
            result: 'fail',
            fail_reason: errMsg,
            time: Date.now() - (round.genStartedAt || Date.now()),
            ...roundReportParams(round),
          })
        }
        console.error('[pollTask] 任务失败，停止轮询:', errMsg)
        state.isGenerating = false
        void loadQuota()
        return null
      }
    } catch (e) {
      consecutiveErrors++
      const msg = e instanceof Error ? e.message : String(e)
      console.warn(`[轮询异常] (${consecutiveErrors}/${MAX_CONSECUTIVE_ERRORS})`, msg)
      // Do not stop immediately on transient errors; timeout is controlled by NO_DATA_TIMEOUT.
    }
  }
}

async function regenerate(roundId: string): Promise<void> {
  const round = state.conversationRounds.find((r) => r.id === roundId)
  if (!round || state.isGenerating) return
  let sessionId = state.currentSessionId
  if (!sessionId) return
  if (!ensureActivityEligible()) return

  state.isGenerating = true
  const { checkBeforeGenerate } = await import('./useAiAccess')
  const canGen = await checkBeforeGenerate(
    (loading) => { if (!loading) state.isGenerating = loading },
    (msg) => { toast.warning(msg) },
  )
  if (!canGen) { state.isGenerating = false; return }

  state.sessionUseGenerateAgain = true
  const regenStartedAt = Date.now()
  try {
    const roundReq = buildRoundRequest(round.prompt, round.params, round.imagesUsed?.length ? round.imagesUsed : undefined)
    if (round.model) roundReq.model = round.model
    const result = await createRoundWithRetry(sessionId, roundReq)
    sessionId = result.sessionId
    const roundData = result.roundData
    state.conversationRounds.push({
      id: roundData.id,
      prompt: round.prompt,
      params: round.params,
      taskId: roundData.provider_task_id || '',
      videoUrl: null,
      status: 'analyzing',
      imagesUsed: round.imagesUsed,
      watermark: state.watermark,
      statusLabel: '正在分析您的需求...',
      createdAt: new Date().toISOString(),
      genStartedAt: Date.now(),
      genReportType: 'generate_again',
    })
    state.sessionGenerateRounds++
    state.roundCounter++
    waitAndPoll(roundData.id, sessionId)
  } catch (err) {
    if (isQuotaExhaustedError(err)) {
      showQuotaModal()
      void loadQuota()
    } else {
      toast.error('再次生成失败: ' + apiErrorMessage(err))
    }
    reportResult({
      type: 'generate_again',
      result: 'fail',
      fail_reason: apiErrorMessage(err),
      time: Date.now() - regenStartedAt,
      ...roundReportParams(round),
    })
  } finally {
    state.isGenerating = false
  }
}

function reEdit(roundId: string): void {
  const round = state.conversationRounds.find((r) => r.id === roundId)
  if (!round) return
  state.sessionUseEditAgain = true
  nextGenAfterReEdit = true
  showPage('home')
  state.promptText = round.prompt
}

async function fetchBlob(url: string): Promise<Blob> {
  const resp = await trackedFetch(url)
  if (!resp.ok) throw new Error(`HTTP ${resp.status}`)
  return resp.blob()
}

function triggerBlobDownload(blob: Blob, filename: string): void {
  const url = URL.createObjectURL(blob)
  const a = document.createElement('a')
  a.href = url
  a.download = filename
  document.body.appendChild(a)
  a.click()
  document.body.removeChild(a)
  URL.revokeObjectURL(url)
}

async function downloadVideo(roundId: string, opts?: { batch?: boolean }): Promise<void> {
  const dlStart = Date.now()
  const reportType = opts?.batch ? 'video_download_all' : 'video_download'
  const round = state.conversationRounds.find((r) => r.id === roundId)
  if (!round?.videoUrl) {
    toast.warning('视频还未准备好')
    reportResult({ type: reportType, result: 'fail', fail_reason: '视频还未准备好', time: Date.now() - dlStart, ...roundReportParams(round) })
    return
  }
  const filename = `wps_video_${roundId}.mp4`
  try {
    const blob = await fetchBlob(`${API_BASE}/proxy-download?url=${encodeURIComponent(round.videoUrl)}`)
    triggerBlobDownload(blob, filename)
    reportResult({ type: reportType, result: 'success', time: Date.now() - dlStart, ...roundReportParams(round) })
    reportVideoUse(round)
  } catch {
    window.open(round.videoUrl, '_blank')
    reportResult({ type: reportType, result: 'success', time: Date.now() - dlStart, ...roundReportParams(round) })
    reportVideoUse(round)
  }
}

function exportShare(): void {
  const latestWithVideo = [...state.conversationRounds].reverse().find((r) => r.videoUrl)
  if (!latestWithVideo) {
    toast.warning('还没有生成完成的视频可分享')
    return
  }
  const url = latestWithVideo.videoUrl as string
  navigator.clipboard
    .writeText(url)
    .then(() => {
      toast.success('视频链接已复制到剪贴板')
    })
    .catch(() => {
      toast.warning('复制失败，请手动复制链接')
    })
}

async function sendFollowUp(text: string | undefined): Promise<void> {
  if (!text?.trim() || state.isGenerating) return
  let sessionId = state.currentSessionId
  if (!sessionId) {
    toast.warning('没有活跃的会话')
    return
  }
  if (!ensureActivityEligible()) return

  if (state.uploadedAudios.length > 0 && state.uploadedImages.length === 0 && state.uploadedVideos.length === 0) {
    toast.warning('音频不能单独作为输入，请同时上传图片或视频')
    return
  }

  state.isGenerating = true
  const { checkBeforeGenerate } = await import('./useAiAccess')
  const canGen = await checkBeforeGenerate(
    (loading) => { if (!loading) state.isGenerating = loading },
    (msg) => { toast.warning(msg) },
  )
  if (!canGen) { state.isGenerating = false; return }

  const params = getCurrentParams()
  const followStartedAt = Date.now()

  const attachments = await uploadAllAttachments()
  if (attachments.imageUrls === null) {
    state.isGenerating = false
    return
  }

  try {
    const roundReq = buildRoundRequest(
      text,
      params,
      attachments.imageUrls.length ? attachments.imageUrls : undefined,
      attachments.videoUrls?.length ? attachments.videoUrls : undefined,
      attachments.audioUrls?.length ? attachments.audioUrls : undefined,
    )
    const result = await createRoundWithRetry(sessionId, roundReq)
    sessionId = result.sessionId
    const roundData = result.roundData
    state.conversationRounds.push({
      id: roundData.id,
      prompt: text,
      params,
      taskId: roundData.provider_task_id || '',
      videoUrl: null,
      status: 'analyzing',
      imagesUsed: attachments.imageUrls,
      videosUsed: attachments.videoUrls || undefined,
      audiosUsed: attachments.audioUrls || undefined,
      watermark: state.watermark,
      statusLabel: '正在分析您的需求...',
      createdAt: new Date().toISOString(),
      genStartedAt: Date.now(),
      genReportType: 'generate',
    })
    revokeAllPreviewUrls()
    state.uploadedImages = []
    state.uploadedVideos = []
    state.uploadedAudios = []
    state.uploadedImageData = null
    state.roundCounter++
    state.sessionGenerateRounds++

    if (state.quota.total > 0 && state.quota.available > 0) {
      state.quota.available--
      state.quota.hold++
    }

    waitAndPoll(roundData.id, sessionId)
  } catch (err) {
    if (isQuotaExhaustedError(err)) {
      showQuotaModal()
      void loadQuota()
    } else {
      toast.error('追加指令失败: ' + apiErrorMessage(err))
    }
    reportResult({
      type: 'generate',
      result: 'fail',
      fail_reason: apiErrorMessage(err),
      time: Date.now() - followStartedAt,
      video_ratio: params.ratio,
      video_pixels: params.resolution,
      video_time: params.duration,
      video_image: attachments.imageUrls.length,
    })
    state.isGenerating = false
  }
}


function loadPexelsVideos(_query?: string, _page?: number): void {
  state.pexelsLoading = false
  state.pexelsData = localDemoVideos
}

function switchTab(t: MaterialTabKey): void {
  const prevTab = state.currentTab
  state.currentTab = t
  if (t === 'my-videos' && prevTab !== 'my-videos') {
    reportShow('my_design')
  }
  if (t === 'my-videos') {
    loadSessions()
    return
  }
  loadPexelsVideos(tabQueries[t] || 'technology', 1)
}

function pexelsPageChange(delta: number): void {
  const newPage = Math.max(1, state.currentPexelsPage + delta)
  loadPexelsVideos(state.currentPexelsQuery, newPage)
}

function getPromptForVideo(index: number): string {
  const item = state.pexelsData[index]
  if (item?.prompt_text) return item.prompt_text
  const pool = promptPools[state.currentPexelsQuery] || promptPools['cinematic trending']
  return pool[index % pool.length]
}

function openMaterialDetail(index: number): void {
  const v = state.pexelsData[index] as PexelsVideo | undefined
  if (!v) return
  state.currentDetailIndex = index
  const localDemo = v.demo_id ? demoById.get(v.demo_id) : null
  const file = v.video_files?.find((f) => f.quality === 'hd') || v.video_files?.[0]
  const media: DetailVideo['media'] = []
  if (localDemo) {
    const refUrls = localDemo.referenceUrls.length
      ? localDemo.referenceUrls
      : localDemo.imageUrl
        ? [localDemo.imageUrl]
        : []
    for (const img of refUrls) {
      media.push({ type: 'image', src: img, label: '图片参考' })
    }
    if (localDemo.videoUrl) {
      media.push({ type: 'video', src: localDemo.videoUrl, label: '示例视频' })
    }
  } else {
    const cover = v.image || v.video_pictures?.[0]?.picture || ''
    if (cover) {
      media.push({ type: 'image', src: cover, label: '封面' })
    }
    if (file?.link) {
      media.push({ type: 'video', src: file.link, label: '示例视频' })
    }
  }

  const primaryVideo = media.find((m) => m.type === 'video')
  const primary = primaryVideo || media[0]
  const detail: DetailVideo = {
    src: primary ? primary.src : '',
    media,
    author: v.user?.name || (localDemo ? '本地 Demo' : 'Pexels Creator'),
    avatar: (v.user?.name || 'P')[0].toUpperCase(),
    duration: (v.duration || 0) + 's',
    prompt: getPromptForVideo(index),
  }
  state.detailVideo = detail
  state.materialDetailOpen = true
}

function closeMaterialDetail(): void {
  state.materialDetailOpen = false
  state.currentDetailIndex = -1
  state.detailVideo = null
}

function fillReferenceImages(urls: string[]): void {
  if (!urls.length) return
  revokeAllPreviewUrls()
  const results: UploadedImage[] = urls.slice(0, MAX_IMAGES).map((url, i) => ({
    id: `ref_${Date.now()}_${i}`,
    name: `参考图${i + 1}`,
    data: url,
  }))
  state.uploadedImages = results
  state.uploadedImageData = state.uploadedImages[0]?.data || null
}

function doMakeSameFromCard(prompt: string, video: PexelsVideo): void {
  state.sessionUseHotPrompt = true
  state.promptText = prompt

  const localDemo = video.demo_id ? demoById.get(video.demo_id) : null
  const imageUrls = localDemo?.referenceUrls?.length
    ? localDemo.referenceUrls
    : [video.image || video.video_pictures?.[0]?.picture || ''].filter(Boolean)

  fillReferenceImages(imageUrls)
  window.scrollTo({ top: 0, behavior: 'smooth' })
}

async function doMakeSame(): Promise<void> {
  if (!state.detailVideo) return
  const prompt = state.detailVideo.prompt
  const imageUrls = state.detailVideo.media.filter((m) => m.type === 'image').map((m) => m.src)
  closeMaterialDetail()
  state.sessionUseHotPrompt = true
  state.promptText = prompt
  fillReferenceImages(imageUrls)
  window.scrollTo({ top: 0, behavior: 'smooth' })
}

function openVideoModal(url: string): void {
  state.videoModalUrl = url
  state.videoModalOpen = true
}

function closeVideoModal(): void {
  state.videoModalOpen = false
  state.videoModalUrl = ''
}

function openHistoryItem(id: string): void {
  const history = getHistory()
  const item = history.find((h) => h.id === id)
  if (!item) return
  if (item.videoUrl) {
    state.conversationRounds = [
      {
        id: item.id,
        prompt: item.prompt,
        params: item.params,
        taskId: item.taskId,
        videoUrl: item.videoUrl,
        status: 'succeeded',
        imagesUsed: [],
      },
    ]
    showPage('session')
  } else if (item.status === 'pending' && item.taskId) {
    state.conversationRounds = [
      {
        id: item.id,
        prompt: item.prompt,
        params: item.params,
        taskId: item.taskId,
        videoUrl: null,
        status: 'pending',
        imagesUsed: [],
      },
    ]
    showPage('session')
    pollTask(item.taskId, item.id)
  }
}

// --- Restore session from route ---

async function restoreSessionFromRoute(sessionId: string): Promise<void> {
  if (state.currentSessionId === sessionId && state.conversationRounds.length > 0) return
  const { useAuth } = await import('./useAuth')
  const { authState } = useAuth()
  if (authState.checked && !authState.loggedIn) {
    showPage('home')
    return
  }
  await openSession(sessionId)
}

export function useAppStore() {
  return {
    state,
    hasContent,
    nowStr,
    showPage,
    toggleDropdown,
    closeAllDropdowns,
    updateRatio,
    updateRes,
    updateDuration,
    updateStyle,
    updateCamera,
    updateModel,
    handleImageUpload,
    handleMediaUpload,
    handleMultiImageUpload,
    removeUploadedImage,
    removeUploadedVideo,
    removeUploadedAudio,
    clearUploadedImage,
    toggleMentionDrop,
    insertImageMention,
    onPromptInput,
    onPromptKeydown,
    clearGhostSuggestion,
    enhancePrompt,
    startGen,
    cancelGeneration,
    regenerate,
    reEdit,
    downloadVideo,
    exportShare,
    sendFollowUp,
    loadPexelsVideos,
    switchTab,
    pexelsPageChange,
    openMaterialDetail,
    closeMaterialDetail,
    doMakeSame,
    doMakeSameFromCard,
    openVideoModal,
    closeVideoModal,
    getHistory,
    openHistoryItem,
    fallbackImgs,
    // Session management
    loadSessions,
    openSession,
    deleteSessionById,
    createNewSession,
    restoreSessionFromRoute,
    restoreDraftAfterLogin,
    clearDraft,
    loadQuota,
    loadActivity,
  }
}
